import {
    ChainedSelectInput,
    Choice,
    Field,
    FieldConditionalLogic,
    FieldConditionalLogicRule, FieldState, FlowValidationRule, FlowValidationRuleCondition, Form,
    Form as FormType,
    Step as StepType, StepDisplayFieldMode
} from '../types';
import {FormState, initialFieldState} from '../components/Form';
import React from 'react';
import moment from 'moment';
import {User} from '../_services/User';
import * as fields from '../components/fields';
require('moment-transform');
window['moment'] = moment;

/**
 * Given FormState and a Field, return the value of the Field from the State
 *
 * @param state
 * @param field
 */
export const fieldValue = (state: FormState | undefined, field: Field): any => {
    if (field === undefined)
        return '';
    if (state === undefined)
        return '';
    return state.fieldValues[field.name];
};

/**
 * Given a field and state, return field's current validation state
 *
 * @param field
 */
export const fieldState = (field: Field, formState: FormState | undefined): FieldState => {
    return formState?.fieldState.hasOwnProperty(field.name) ?
        formState.fieldState[field.name]
        :
        initialFieldState;
};

/**
 * Given FormState, Form and a Field ID, return a string that represents the fields "value"
 * For example, a dropdown has an option value and option text (<option value="option_value">Option Text</option>),
 * this method would return the Option Value
 * fields like Select have values that are objects, this method extracts the text from that object
 *
 * @param state
 * @param form
 * @param fieldId
 */
export const fieldValueText = (state: FormState | undefined, form: Form | undefined, fieldId: number | string): string | string[] => {
    if (form === undefined) {
        return '';
    }

    // checkboxes and chainedselects can have IDs in the form of X.Y, the fields value
    // is store in state as X
    const actualFieldId = String(fieldId).split('.')[0];
    const field = form.fieldMap[actualFieldId];
    const value = fieldValue(state, field);

    if (value === undefined) {
        return '';
    }

    if (field !== undefined) {
        if (field.type === 'select' || field.type === 'radio' || field.type === 'workflow_user') {
            return value?.value || '';
        } else if (field.type === 'checkbox' || field.type === 'multiselect' || field.type === 'workflow_multi_user') {
            return value ? value.map((item: Choice) => item.value) : [];
        } else if (field.type === 'chainedselect') {
            return (
                value instanceof Object &&
                value.hasOwnProperty(fieldId) &&
                value[fieldId] instanceof Object &&
                value[fieldId].hasOwnProperty('value')
            ) ? value[fieldId].value : '';
        }
    }

    return value;
};

/**
 * Given FormState, Form and a Field ID, return an object suitable for displaying the Fields "Display Value"
 * For example, a dropdown has an option value and option text (<option value="option_value">Option Text</option>),
 * this method would return the Option Text
 *
 * @param state
 * @param form
 * @param fieldId
 */
/*
export const fieldValueLabel = (state: FormState, form: Form, fieldId: number): JSX.Element | string;
export const fieldValueLabel = (state: FormState, field: Field): JSX.Element | string;
 */
export const fieldValueLabel = (state: FormState, param1: Form | Field, param2?: number): JSX.Element | string => {
    // @ts-ignore - not ideal, but if param1 has form_id property, then its a Field, otherwise its a Form
    const field = (param1.hasOwnProperty('formId')) ? param1 : param1.fieldMap[param2];
    let value = fieldValue(state, field);
    if (value === undefined) {
        return '';
    }

    if (field.type === 'select' || field.type === 'checkbox' || field.type === 'radio' || field.type === 'workflow_user') {
        //console.log('returning label', value.label);
        return value.label;
    } else if (field.type === 'workflow_multi_user' || field.type === 'multiselect') {
        return (value instanceof Array) ?
            <div>{value.map((item: Choice, index: number) => <div key={index}>{item.label}</div>)}</div> : '';
    } else if (field.type === 'chainedselect') {
        return (
            <div>
                {field.inputs.map((input: ChainedSelectInput) => (
                        <div key={input.id}>
                            <strong>{input.label}:</strong>
                            {value.hasOwnProperty(input.id) ? value[input.id].label : ''}
                        </div>
                    )
                )}
            </div>
        );
    } else if (field.type === 'fileupload') {
        const filename = baseName(value);
        return (
            <a href={`/p/flow/api/download-file?url=${value}&filename=${filename}`} target="_blank">
                {filename}
            </a>
        );
    } else if (field.type === 'form') {
        if (field?.flowCustomDisplayComponent === 'FileUploadWithRevisions' && value?.length > 0) {
            // grab the latest revision and show the "file"
            const fileFieldId = field?.flowCustomDisplayComponentConfig?.fileFieldId;
            const revisionFieldId = field?.flowCustomDisplayComponentConfig?.revisionFieldId;
            if (fileFieldId && revisionFieldId) {
                // sort array ascending by revision
                const revisionFieldKey = 'input_' + revisionFieldId;
                const sortedValues = [...value].sort((a: any, b: any) => (a[revisionFieldKey] > b[revisionFieldKey]) ? 1 : -1);

                // pop off last item
                const latestRevision = sortedValues.pop();

                const fileUrl = latestRevision['input_' + fileFieldId];
                const revision = latestRevision['input_' + revisionFieldId];
                const createdOn = moment(latestRevision['date_created']).format('M/D/YYYY');
                const filename = baseName(fileUrl);

                return (
                    <span>
                        <a href={`/p/flow/api/download-file?url=${fileUrl}&filename=${filename}`} target="_blank">
                            {filename}
                        </a>
                        <small className="pl-2">
                            (Rev. {revision} on {createdOn})
                        </small>
                    </span>
                );
            }
        }
        return '';
    }
    return value;
};

/**
 * Give a URL, return the name of the file. This is used for getting uploaded filename from URL string
 *
 * @param url
 */
export const baseName = (url: string) => {
    let base = url.substring(url.lastIndexOf('/') + 1);
    /*
    if (base.lastIndexOf('.') !== -1)
        base = base.substring(0, base.lastIndexOf('.'));
     */
    return base;
};

// @ts-ignore - ignoring unused parameter
const evaluateValidationRuleCondition = (field: Field, condition: FlowValidationRuleCondition, conditionFieldValue: string | string[], form: FormType, formState: FormState) => {
    let testValue = condition.testValue;

    // eval if testValue "looks" like a function
    if (typeof testValue === 'string' && testValue.match(/\(function\(/)) {
        try {
            // tslint:disable-next-line:no-eval
            testValue = eval(condition.testValue);
        } catch (e) {
            console.log(e);
            testValue = '';
        }
    }

    console.log('testValue is', testValue);
    console.log('conditionFieldValue is', conditionFieldValue);

    testValue = field.type === 'date' ? dateStringTransformToEpoch(testValue) : testValue;
    let fieldValue = field.type === 'date' ? dateStringToEpoch(conditionFieldValue as string) : conditionFieldValue;

    console.log('field value for ', field.id, fieldValue, testValue);
    if (field.type === 'number' && typeof fieldValue === 'string') {
        fieldValue = fieldValue.replace(/[$,]/g, '');
        try {
            const newValue = Number.parseFloat(fieldValue);
            if (!Number.isNaN(newValue)) {
                fieldValue = newValue;
            }

            const newTestValue = Number.parseFloat(testValue);
            if (!Number.isNaN(newTestValue)) {
                testValue = newTestValue.toString();
            }
        } catch (e) {

        }
    }

    console.log('evaluating fieldValue and testValue', fieldValue, testValue);

    switch (condition.operator) {
        case 'equals':
            return (fieldValue == testValue);
        case 'not equals':
            return (fieldValue != testValue);
        case '>':
            return parseFloat(fieldValue as string) > parseFloat(testValue);
        case '<':
            return parseFloat(fieldValue as string) < parseFloat(testValue);
        case '>=':
            return parseFloat(fieldValue as string) >= parseFloat(testValue);
        case '<=':
            return parseFloat(fieldValue as string) <= parseFloat(testValue);
        case 'regex':
            if (Array.isArray(fieldValue)) {
                return false;
            }
            return (new RegExp(testValue)).test(fieldValue as string);
        default:
            return false;
    }
};

const dateStringToEpoch = (dateValue: string) => {
    return moment(dateValue).unix();
};

const dateStringTransformToEpoch = (dateValue: string) => {
    // @ts-ignore
    return moment().transform(dateValue).unix();
};

/**
 * Form validation. Takes FormState and applies rules
 *
 * Note, this method currently updates the form state using passed parameter, should be updated to return a new state
 *
 * @param form
 * @param formState
 * @param setFormState
 * @param filterFields
 */
export const validate = (form: FormType, formState: FormState, setFormState: any, filterFields: string[] = []): boolean => {
    const fieldState = formState.fieldState;
    let isValid: boolean = true;
    console.log('validate called for ', form, 'with', formState);

    // TODO: update to not validate fields filtered by: filterDisplayFields

    // going to start with a naive approach, this will be made more robust over time
    form.fields
        // filter out fields that can't be edited
        // if array is empty, all fields are returned
        .filter((field: Field) => (
            filterFields.length === 0 || filterFields.find((fieldId: string) => fieldId === String(field.id)) !== undefined
        ))
        // filter out fields that aren't visible
        .filter((field: Field) => formState.visibility.hasOwnProperty(field.name) && formState.visibility[field.name])
        .forEach((field: Field) => {
            fieldState[field.name] = {...initialFieldState};

            let fieldValidationMessage = 'is Required';
            let value: any = fieldValueText(formState, form, field.id);

            // console.log('validating field', field, value, field.isRequired, (field.isRequired && (value === undefined || !value)));

            if (field.flowValidation && field.flowValidation instanceof Array && field.flowValidation.length > 0) {

                // custom validation logic
                field.flowValidation.some((rule: FlowValidationRule) => {
                    // required if: region !== 'Retail' and type !== 'Concession'
                    if (rule.type === 'every') {
                        const passed = rule.conditions.every((condition: FlowValidationRuleCondition) => {
                            // condition.testValue is [stupidly] being overwritten, need to save and restore it at each iteration
                            const originalConditionTestValue = condition.testValue;

                            const conditionFieldValue = fieldValueText(formState, form, condition.fieldId);
                            const conditionField = form.fieldMap.hasOwnProperty(condition.fieldId) ?
                                form.fieldMap[condition.fieldId]
                                :
                                field;
                            const matches = condition.testValue.match(/^\{(.*?)\}$/);
                            if (matches && matches.length > 1) {
                                const matchedFieldId = matches[1];

                                // NOTE - chose to use fieldValueLabel over fieldValueText as my use case was comparing
                                // a dropdown/text field to a user field, and the user field was returning the WP ID
                                // and not the email.
                                // NOTE 2 - using fieldValueLabel was causing problems with number fields, to preserve backward
                                // compatibility, explicitly check field type and use the appropriate getter
                                if (form.fieldMap.hasOwnProperty(matchedFieldId) && form.fieldMap[matchedFieldId].type === 'number') {
                                    condition.testValue = fieldValueText(formState, form, matchedFieldId) as string;
                                } else {
                                    condition.testValue = fieldValueLabel(formState, form, matches[1] as any) as string;
                                }
                            }
                            const evaluationResult = evaluateValidationRuleCondition(conditionField, condition, conditionFieldValue, form, formState);
                            condition.testValue = originalConditionTestValue;
                            return evaluationResult;
                        });

                        console.log('passed', passed);
                        if (!passed) {
                            console.log('rule did not pass, udating field');
                            field.isRequired = true;
                            fieldValidationMessage = `${rule.message}`;
                            if (rule?.clearValueOnFail) value = ''; // clear value so conditional below will return false

                            // field ranges throw off validation logic below;
                            if (field.type === 'number') {
                                // @ts-ignore
                                field.rangeMin = undefined;
                                // @ts-ignore
                                field.rangeMax = undefined;
                            } else if (field.type === 'form') {
                                // NOTE - just cause field validation failed, there is special logic for nested forms, need to "trip" that logic
                                field.gpnfEntryLimitMin = 9999;
                            }
                        } else {
                            field.isRequired = false;
                        }
                        return false;
                    } else if (rule.type === 'none') {
                        const passed = rule.conditions.every((condition: FlowValidationRuleCondition) => {
                            const conditionFieldValue = fieldValueText(formState, form, condition.fieldId);
                            const conditionField = form.fieldMap.hasOwnProperty(condition.fieldId) ?
                                form.fieldMap[condition.fieldId]
                                :
                                field;
                            const matches = condition.testValue.match(/^\{(.*?)\}$/);
                            if (matches && matches.length > 1) {
                                condition.testValue = fieldValueLabel(formState, form, matches[1] as any) as string;
                            }
                            return !evaluateValidationRuleCondition(conditionField, condition, conditionFieldValue, form, formState);
                        });

                        if (passed) {
                            field.isRequired = true;
                            fieldValidationMessage = `${rule.message}`;
                            if (rule?.clearValueOnFail) value = ''; // clear value so conditional below will return false

                            // field ranges throw off validation logic below;
                            if (field.type === 'number') {
                                // @ts-ignore
                                field.rangeMin = undefined;
                                // @ts-ignore
                                field.rangeMax = undefined;
                            } else if (field.type === 'form') {
                                // NOTE - just cause field validation failed, there is special logic for nested forms, need to "trip" that logic
                                field.gpnfEntryLimitMin = 9999;
                            }
                        } else {
                            field.isRequired = false;
                        }
                        return false;
                    } else if (rule.type === 'any') {
                        const passed = rule.conditions.some((condition: FlowValidationRuleCondition) => {
                            const conditionFieldValue = fieldValueText(formState, form, condition.fieldId);
                            const conditionField = form.fieldMap.hasOwnProperty(condition.fieldId) ?
                                form.fieldMap[condition.fieldId]
                                :
                                field;
                            const matches = condition.testValue.match(/^\{(.*?)\}$/);
                            if (matches && matches.length > 1) {
                                condition.testValue = fieldValueLabel(formState, form, matches[1] as any) as string;
                            }
                            return evaluateValidationRuleCondition(conditionField, condition, conditionFieldValue, form, formState);
                        });

                        if (passed) {
                            field.isRequired = true;
                            fieldValidationMessage = `${rule.message}`;

                            // field ranges throw off validation logic below;
                            if (field.type === 'number') {
                                // @ts-ignore
                                field.rangeMin = undefined;
                                // @ts-ignore
                                field.rangeMax = undefined;
                            } else if (field.type === 'form') {
                                // NOTE - just cause field validation failed, there is special logic for nested forms, need to "trip" that logic
                                field.gpnfEntryLimitMin = 9999;
                            }
                            if (rule?.clearValueOnFail) value = ''; // clear value so conditional below will return false
                        } else {
                            field.isRequired = false;
                        }
                        return false;
                    }
                    return true;
                });

            }
console.log('handling validation for ', field);
            if (field.type === 'chainedselect' && field.isRequired) {
                // not ideal for validating chainedselects, but will have to work for now...
                const numberOfInputs: number = field.inputs.length;
                let countValidInputs: number = 0;
                const chainedSelectValue = fieldValue(formState, field);
                if (chainedSelectValue instanceof Object) {
                    Object.keys(chainedSelectValue).forEach((prop: string) => {
                        if (
                            chainedSelectValue[prop] !== null &&
                            chainedSelectValue[prop] instanceof Object &&
                            chainedSelectValue[prop].hasOwnProperty('value') &&
                            chainedSelectValue[prop].value
                        ) {
                            countValidInputs++;
                        }
                    });
                }
                if (numberOfInputs !== countValidInputs) {
                    fieldState[field.name].invalid = true;
                    fieldState[field.name].errors = [`${field.label} ${fieldValidationMessage}`];
                    isValid = false;
                }
            } else if (field.type === 'workflow_multi_user') {
                const userValue = fieldValue(formState, field);
                const minUsers = field.minUsers || 1;
                if (field.isRequired && (userValue === undefined || userValue.length < minUsers)) {
                    fieldState[field.name].invalid = true;
                    fieldState[field.name].errors = [`Minimum of ${minUsers} required`];
                    isValid = false;
                }
            } else if (field.type === 'form') {
                const nestedValues = fieldValue(formState, field);

                if (field.isRequired) {
                    if (nestedValues === undefined || nestedValues.length < field.gpnfEntryLimitMin) {
                        fieldState[field.name].invalid = true;
                        fieldState[field.name].errors = [`${field.label} ${fieldValidationMessage}`];
                        isValid = false;
                    }
                }

                if (nestedValues !== undefined && nestedValues.length > 0) {
                    console.log('validating nested entries');
                    /*
                    // validate the nested items
                    const allValuesSuccess = nestedValues.every((entryValues: any) => {
                        console.log('validating', entryValues);
                        let nestedFormState = {
                            ...initialFormState,
                            fieldValues: entryValues,
                            visibility: applyVisibilityRules(field.nestedForm, undefined, {
                                ...initialFormState,
                                fieldValues: entryValues
                            }, false, false),
                        };
                        return validate(field.nestedForm, nestedFormState, (state: FormState) => {
                            console.log('nested validation passed state', state);
                        }, []);
                    });

                    if (!allValuesSuccess) {
                        fieldState[field.name].invalid = true;
                        fieldState[field.name].errors = ['One or more entries failed validation'];
                        isValid = false;
                    }
 */
                }
            } else if (field.type === 'checkbox' && field.isRequired) {
                if (value.length === 0) {
                    fieldState[field.name].invalid = true;
                    fieldState[field.name].errors = [`${field.label} ${fieldValidationMessage}`];
                    isValid = false;
                }
            } else if (field.isRequired && field.type === 'number' && field.rangeMin !== undefined && value.replace(/[$,]/g, '') < field.rangeMin) {
                fieldState[field.name].invalid = true;
                fieldState[field.name].errors = [`${field.label} must be greater than or equal to ${field.rangeMin}`];
                isValid = false;
            } else if (field.isRequired && field.type === 'number' && field.rangeMax !== undefined && value.replace(/[$,]/g, '') > field.rangeMax) {
                fieldState[field.name].invalid = true;
                fieldState[field.name].errors = [`${field.label} must be less than or equal to ${field.rangeMax}`];
                isValid = false;
            } else if (field.isRequired && field.type === 'multiselect' && value && Array.isArray(value) && value.length === 0) {
                fieldState[field.name].invalid = true;
                fieldState[field.name].errors = [`${field.label} ${fieldValidationMessage}`];
                isValid = false;
            } else if (field.isRequired && (value === undefined || !value)) {
                // console.log('setting field invalid', field.name, fieldValidationMessage);
                fieldState[field.name].invalid = true;
                fieldState[field.name].errors = [`${field.label} ${fieldValidationMessage}`];
                isValid = false;
            }
        });

    // console.log('validation setting formState', fieldState);
    setFormState((state: FormState) => ({
        ...state,
        fieldState
    }));

    return isValid;
};

/**
 * Build state object representing the visibility of all fields on a Form based on current form state
 *
 * @param form
 * @param step
 * @param formState
 * @param showAdministrativeFields
 */
export const applyVisibilityRules = (form: FormType, step: StepType | undefined, formState: FormState, showAdministrativeFields: boolean, showPrivateFields: boolean): VisibilityState => {
    let visibilityState: any = {};

    form.fields.forEach((field: Field) => {
        // evaluate the field's conditional logic
        let shouldShow = true;

        if (field.conditionalLogic) {
            let matchCount = 0;
            field.conditionalLogic.rules.forEach((rule: FieldConditionalLogicRule) => {
                const targetFieldValue = fieldValueText(formState, form, rule.fieldId);

                if (['is', 'isnot'].includes(rule.operator)) {
                    const valueIsEqualToTargetField = (
                        (targetFieldValue instanceof Array && targetFieldValue.find((item: string) => item === rule.value) !== undefined) ||
                        (targetFieldValue instanceof Array && targetFieldValue?.length === 0 && rule.value === null) || /* handle the case of an empty multi-select in conditional logic */
                        targetFieldValue === rule.value
                    );

                    if ((rule.operator === 'is' || rule.operator === 'contains') && valueIsEqualToTargetField) {
                        matchCount++;
                    } else if (rule.operator === 'isnot' && !valueIsEqualToTargetField) {
                        matchCount++;
                    }
                } else if (rule.operator === 'starts_with' && typeof targetFieldValue === 'string' && (new RegExp('^' + rule.value, 'i')).test(targetFieldValue as string)) {
                    matchCount++;
                } else if (rule.operator === 'contains' && typeof targetFieldValue === 'string' && (new RegExp(rule.value, 'i')).test(targetFieldValue as string)) {
                    matchCount++;
                } else if ((rule.operator === '>' || rule.operator === '<') && targetFieldValue && !(targetFieldValue instanceof Array)) {
                    const numberValue = parseFloat(targetFieldValue.replace(/[$,]/g, ''));
                    const ruleValue = parseFloat(rule.value);

                    console.log('applyVisibilityRules - number comparison - numberValue: ', numberValue, ' ruleValue: ', ruleValue, ' rule: ', rule);

                    if (isNaN(numberValue) || isNaN(ruleValue)) {
                        console.error('applyVisibilityRules - number comparison - numberValue or ruleValue isNaN');
                    } else {
                        if (
                            (rule.operator === '>' && numberValue > ruleValue) ||
                            (rule.operator === '<' && numberValue < ruleValue)
                        ) {
                            matchCount++;
                        }
                    }
                }
            });

            const ruleTrue = (
                (field.conditionalLogic.logicType === 'all' && matchCount === field.conditionalLogic.rules.length) ||
                (field.conditionalLogic.logicType === 'any' && matchCount > 0)
            );
            shouldShow = ((!ruleTrue && field.conditionalLogic.actionType === 'hide') || (ruleTrue && field.conditionalLogic.actionType === 'show'));
        }

        // todo: merge with above logic
        if (field.flowConditionalLogic && Array.isArray(field.flowConditionalLogic) && field.flowConditionalLogic.length > 0) {

            let flowLogicRuleMatch = false;
            field.flowConditionalLogic.forEach((logic: FieldConditionalLogic) => {
                if (logic?.rules?.length > 0) {
                    const conditionalLogic: FieldConditionalLogic = logic;

                    let matchCount = 0;
                    conditionalLogic.rules.forEach((rule: FieldConditionalLogicRule) => {
                        let targetFieldValue = rule.fieldId === 'step' ? (step ? (step?.id || step?.name) : '') : fieldValueText(formState, form, rule.fieldId);

                        // NOTE - this breaks so many rules and really needs to be updated....
                        let ruleValue: string = rule.value;
                        if (ruleValue === '{user.email}') {
                            ruleValue = User.getInstance().getEmail();
                            // @ts-ignore
                            const fieldId: number = rule.fieldId as number;
                            targetFieldValue = fieldValueLabel(formState, form, fieldId) as string;
                        } else if (ruleValue && /^\{(.*?)\}$/.test(ruleValue)) {
                            const matches = ruleValue.match(/^\{(.*?)\}$/);
                            if (matches && matches.length > 1) {
                                // @ts-ignore
                                const matchedFieldId = matches[1] as number;
                                ruleValue = fieldValueLabel(formState, form, matchedFieldId) as string;
                                console.log('set ruleValue to', ruleValue);
                            }
                        }

                        // typeof ruleValue === 'string'
                        // Array.isArray(ruleValue)
                        if (rule.operator === 'is' && typeof targetFieldValue === 'string' && targetFieldValue === ruleValue) {
                            matchCount++;
                        } else if (rule.operator === 'is' && Array.isArray(targetFieldValue) && targetFieldValue.includes(rule.value)) {
                            matchCount++;
                        } else if (rule.operator === 'starts_with' && typeof targetFieldValue === 'string' && (new RegExp('^' + rule.value, 'i')).test(targetFieldValue as string)) {
                            matchCount++;
                        } else if (rule.operator === 'contains' && typeof targetFieldValue === 'string' && (new RegExp(rule.value, 'i')).test(targetFieldValue as string)) {
                            matchCount++;
                        } else if (rule.operator === 'contains' && Array.isArray(targetFieldValue)  && targetFieldValue.some(v => (new RegExp(rule.value, 'i')).test(v))) {
                            matchCount++;
                        } else if (rule.operator === 'isnot' && typeof targetFieldValue === 'string' && targetFieldValue !== rule.value) {
                            matchCount++;
                        } else if (rule.operator === 'isnot' && Array.isArray(targetFieldValue) && !targetFieldValue.includes(rule.value)) {
                            matchCount++;
                        } else if ((rule.operator === '>' || rule.operator === '<') && targetFieldValue && !(targetFieldValue instanceof Array)) {
                            console.log(field, rule.fieldId);
                            // @ts-ignore
                            const numberValue = targetFieldValue.replace(/[$,]/g, '');

                            if (
                                (rule.operator === '>' && numberValue > rule.value) ||
                                (rule.operator === '<' && numberValue < rule.value)
                            ) {
                                console.log('comparison operator match');
                                matchCount++;
                            }
                        }
                    });

                    const ruleTrue = (
                        (conditionalLogic.logicType === 'all' && matchCount === conditionalLogic.rules.length) ||
                        (conditionalLogic.logicType === 'any' && matchCount > 0)
                    );

                    flowLogicRuleMatch = flowLogicRuleMatch || (ruleTrue && conditionalLogic.actionType === 'show');
                }
            });

            shouldShow = (shouldShow && flowLogicRuleMatch);
        }

        if (showAdministrativeFields === false && field.visibility === 'administrative') {
            shouldShow = false;
        }

        if (showPrivateFields === false && field.isPrivate) {
            shouldShow = false;
        }

        if (field.visibility === 'hidden') {
            shouldShow = false;
        }

        // if field belongs to a section, apply the section's visibility to current field
        // this allows for fields to have their own visibility logic when a section is shown
        if (field.sectionFieldId) {
            const sectionField = form.fieldMap[field.sectionFieldId];
            if (visibilityState.hasOwnProperty(sectionField.name)) {
                // console.log('applying section visibility logic to ' + field.label, visibilityState);
                shouldShow = shouldShow && visibilityState[sectionField.name];
            }
        }

        visibilityState[field.name] = shouldShow;
    });

    return visibilityState
};

/**
 * Given a string of text that is supposed to represent a number (i.e. currency, number with thousands separator, etc...),
 * strip everything but digits and decimal and return a float
 *
 * @param numberText string representing a number
 */
export const numberFromText = (numberText: string): any => {
    // treat the text values Yes and No as the numbers 1 and 0
    if (numberText === 'Yes') return 1;
    if (numberText === 'No') return 0;

    const n = String(numberText).replace(/[^\d\.\-]/g, '');
    if (!n) {
        return 0;
    }
    return n;
};

/**
 * Format a number according to passed Field definition. i.e., apply thousands separator, currency, etc..
 *
 * @param field
 * @param value
 */
export const formatNumberField = (field: Field, value: number): string => {
    const formatted: string = numberFormat(value, field.numberFormatDecimal);
    if (field.numberFormat === 'currency') {
        return '$' + formatted;
    }
    return formatted;
};

// https://locutus.io/php/strings/number_format/
export const numberFormat = (number: number, decimals: number, dec_point?: string, thousands_sep?: string): string => {
    let n = !isFinite(+number) ? 0 : +number,
        prec = !isFinite(+decimals) ? 0 : Math.abs(decimals),
        sep = (typeof thousands_sep === 'undefined') ? ',' : thousands_sep,
        dec = (typeof dec_point === 'undefined') ? '.' : dec_point,
        toFixedFix = function (n: number, prec: number) {
            // Fix for IE parseFloat(0.55).toFixed(0) = 0;
            const k = Math.pow(10, prec);
            const kPlusOne = Math.pow(10, prec + 1);
            //return Math.round((Math.round(n * kPlusOne) / kPlusOne) * k) / k;
            return Math.round( (n*k).toFixed(1) ) / k;
        },
        s = (prec ? toFixedFix(n, prec) : Math.round(n)).toString().split('.');
    if (s[0].length > 3) {
        s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
    }
    if ((s[1] || '').length < prec) {
        s[1] = s[1] || '';
        s[1] += new Array(prec - s[1].length + 1).join('0');
    }
    return s.join(dec);
}

export const removeNumberFormat = (number: string): number => {
    return Number(
        number
            ?.replace(/^\$/, '')
            ?.replace(/[^\d\.\-]/g, '')
    );
}

/**
 * Array filter function which applies the Field Display Mode (display all fields except or display selected fields)
 *
 * @param mode
 * @param fieldIds
 * @return Function which can be passed to Array.filter
 */
export const filterDisplayFields = (mode?: StepDisplayFieldMode, fieldIds?: number[]) => (field: Field): any => {
    if (!mode) {
        return true;
    }

    if (!fieldIds || fieldIds.length === 0) {
        return true;
    }

    const fieldFound = (fieldIds.find((fieldId: number) => String(fieldId) === String(field.id)) !== undefined);

    // if field is part of a section, apply same logic to field as applied to section....
    // ONLY if mode is selected_fields
    // JDM - only apply this logic IF the section is in list of fields, this should be safe for existing logic...
    if (
        mode === 'selected_fields' &&
        field.sectionFieldId &&
        // tslint:disable-next-line:triple-equals - fieldId is number, sectionFieldId is string, must use double equal to ensure match
        fieldIds.findIndex((fieldId) => fieldId == field.sectionFieldId) !== -1
    ) {
        // @ts-ignore - don't have access to the Section's field object, passing a vanilla object is okay here as we have
        //              visibility to how its being used
        return filterDisplayFields(mode, fieldIds)({id: field.sectionFieldId});
    }

    return (
        (mode === 'all_fields_except' && !fieldFound) ||
        (mode === 'selected_fields' && fieldFound) ||
        mode === 'all_fields'
    )
};

/**
 * Given a formula mergetag (i.e. {FieldLabel:ID} or {FieldLabel:ID:Operation=NestedFieldID}), replace tags with values
 * from Form state and return
 *
 * @param formula
 * @param form
 * @param formState
 */
export const calculationFormulaFromForm = (formula: string, form: Form, formState: FormState): string => {
    // @ts-ignore
    return formula.replace(/{.*?:(\d+)(:([a-zA-Z]+)=(\d+))?(\?(.*?)=(.*?),(.*?))?}/g, (
        _match: any,
        matchedFieldId: number,
        _dontCare1: any,
        operation: string | undefined,
        nestedFieldId: number | undefined,
        _dontCare2: any,
        // @ts-ignore
        attributeName: string,
        // @ts-ignore
        attributeValue: string,
        // @ts-ignore
        attributeDefaultValue: string
    ) => {
        console.log('calculationFormulaFromForm - operation', operation);
        if (operation === undefined) {
            // @ts-ignore
            return String(numberFromText(fieldValueText(formState, form, matchedFieldId)));
        } else {
            const nestedFormField = form.fieldMap[matchedFieldId];
            console.log('calculationFormulaFromForm - get nested form field', nestedFormField, nestedFieldId);
            // operations only work on fields of type nestedform
            if (nestedFormField.type === 'form' && nestedFieldId) {
                const nestedFieldValue = fieldValue(formState, nestedFormField);
                console.log('calculationFormulaFromForm - get nestedFieldValue', nestedFieldValue);
                if (operation === 'sum') {
                    if (nestedFieldValue instanceof Array) {
                        return String(nestedFieldValue.reduce((acc: number, item: any) => {
                            // @ts-ignore
                            return acc + Number(numberFromText(fieldValueText({fieldValues: item}, nestedFormField.nestedForm, nestedFieldId)));
                        }, 0));
                    }
                } else if (operation === 'count') {
                    return (nestedFieldValue instanceof Array) ? nestedFieldValue.length : 0;
                }
            }
        }
        return '0';
    });
};

/**
 * Given an array of fieldIds, create an object of calculated values to be passed to FormState.fieldValues
 *
 * @param form
 * @param formState
 * @param fieldIds
 */
export const calculationFieldValues = (form: Form, formState: FormState, fieldIds: number[]): object => {

    return fieldIds.reduce((acc: object, fieldId: number) => {
        const field = form.fieldMap[fieldId];
        let calculatedValue: any = '';

        if (field.flowAdvancedFormula) {
            try {
                // tslint:disable-next-line:no-eval
                calculatedValue = eval(field.flowAdvancedFormula);
            } catch (e) {
                console.log('exception', e);
                calculatedValue = '';
            }
        } else {
            // match either {FieldLabel:ID} or {FieldLabel:ID:Operation=NestedFieldID}
            // replace with current values in respective fields
            const formula = calculationFormulaFromForm(field.calculationFormula, form, formState);

            // formula should contain basic math, do js eval to get value
            try {
                // tslint:disable-next-line:no-eval
                calculatedValue = eval(formula);
                calculatedValue = parseFloat(calculatedValue);
                if (isNaN(calculatedValue)) {
                    calculatedValue = '';
                } else {
                    calculatedValue = formatNumberField(field, calculatedValue);
                }
            } catch (e) {
                calculatedValue = '';
            }
        }

        return {...acc, [`input_${field.id}`]: calculatedValue};
    }, {});
};

/**
 * Given the state of the From, return a boolean indicating whether the Form is busy (true) and should
 * disable submit functionality
 * @param formState
 */
export const formIsBusy = (formState: FormState): boolean => {
    return (Object.keys(formState.busyFields).filter((prop: string) => formState.busyFields[prop]).length > 0)
};

/**
 * Given a string containing a merge tag of the form {Field Name:FieldID}, replace tag with Form field value
 * from passed state. If merge tag is not found, then the original input is returned.
 *
 * @param tag
 * @param form
 * @param formState
 */
export const parseMergeTag = (tag: string, form: Form, formState: FormState): string => {
    if (tag && tag.match && tag.match(/^{.*?}$/)) {
        return tag.replace(/{.*?:(\d+|id)}/, (_match: any, matchedFieldId: number|'id'): any => {
            if (matchedFieldId === 'id') {
                return formState?.fieldValues?.id || '';
            } else {
                return fieldValueText(formState, form, matchedFieldId);
            }
        });
    } else {
        return tag;
    }
};

const componentMap: any = {
    text: fields.Text,
    select: fields.Select,
    multiselect: fields.MultiSelect,
    section: fields.Section,
    workflow_user: fields.WorkflowUser,
    workflow_multi_user: fields.WorkflowMultiUser,
    number: fields.Number,
    phone: fields.Phone,
    password: fields.Password,
    email: fields.Email,
    quantity: fields.Quantity,
    textarea: fields.Textarea,
    date: fields.Datepicker,
    checkbox: fields.Checkbox,
    radio: fields.Radio,
    chainedselect: fields.ChainedSelect,
    form: fields.NestedForm,
    html: fields.Html,
    fileupload: fields.FileUpload,
    workflow_discussion: fields.Discussion,
    hidden: fields.Hidden,

    // custom fields
    InvoiceSearch: fields.InvoiceSearch,
    TorqueInvoiceSearch: fields.TorqueInvoiceSearch,
    TorqueBuLookup: fields.TorqueBuLookup,
    ContingencySearch: fields.ContingencySearch,
    RangeSelect: fields.RangeSelect,
    Link: fields.Link,
    Image: fields.Image,
    NestedFormGrid: fields.NestedFormGrid,
    NestedFormGridModal: fields.NestedFormGridModal,
    NestedFormEntryView: fields.NestedFormEntryView,
    DatetimePicker: fields.DatetimePicker,
    FileUploadWithRevisions: fields.FileUploadWithRevisions,
    Wysiwyg: fields.Wysiwyg,
    CheckboxButtons: fields.CheckboxButtons,
    RadioButtons: fields.RadioButtons,
    PackagingProvisionLookup: fields.PackagingProvisionLookup,
    WorkflowUserEnhanced: fields.WorkflowUserEnhanced,
    LegacySalesOrderText: fields.LegacySalesOrderText,
};

/**
 * Given a field, return the React component responsible for redering the field
 * @param field
 */
export const getComponentForField = (field: Field) => {
    const fieldTypeKey = (field.flowCustomDisplayComponent && componentMap.hasOwnProperty(field.flowCustomDisplayComponent)) ?
        field.flowCustomDisplayComponent :
        field.type;
    return componentMap[fieldTypeKey];
}

/**
 * Given a field, return if there is a React component defined for rendering
 * @param field
 */
export const isComponentValidForField = (field: Field) => {
    return Object.keys(componentMap).includes(field.type)
}

/**
 * Update FormState to indicate the passed field is busy or "working"
 *
 * @param setFormState
 * @return function with the following signature:
 * (field: Field): (isBusy: boolean) => void
 */
export const formComponentHandleIsBusy = (setFormState: any) => (field: Field): (isBusy: boolean) => void => {
    return ((isBusy: boolean) => {
        setFormState((state: FormState): FormState => {
            return {
                ...state,
                busyFields: {
                    ...state.busyFields,
                    [field.name]: isBusy
                }
            };
        });
    });
};

/**
 * Handles field onChange events inside a Form component
 *
 * in addition to setting a fields value in onchange, any part of the form
 * state can also be updated using the second parameter
 *
 * @param form
 * @param setFormState
 * @return function with the following signature:
 * (field: Field): (value: any, state?: any) => void
 *
 */
export const formComponentHandleOnChange = (form: any, setFormState: any) => (field: Field): (value: any, state?: any) => void => {
    return (value: any, state: any = {}) => {
        let newValue = value;

        // if field has a maxlength, enforce it
        if (field.maxLength && newValue.length > field.maxLength) {
            newValue = newValue.substring(0, field.maxLength);
        }

        // format number fields (currency formatting is handled in blur event)
        if (field.type === 'number') {
            newValue = numberFromText(newValue);
        }

        setFormState((currentState: FormState): FormState => {
            const newState = {
                ...currentState,
                fieldValues: {
                    ...currentState.fieldValues,
                    [field.name]: newValue,
                    ...state,
                }
            };

            // update any calculation fields
            console.log('form onchange calculationFieldValues pass 1');
            let calculatedFields = calculationFieldValues(form, newState, form.calculationFields);

            const updatedState = {
                ...newState,
                fieldValues: {
                    ...newState.fieldValues,
                    ...calculatedFields,
                }
            };

            // run twice to catch any circular dependencies
            console.log('form onchange calculationFieldValues pass 2');
            calculatedFields = calculationFieldValues(form, updatedState, form.calculationFields)

            // custom onChange handler
            let customOnChangeState = {};
            if (field?.flowCustomDisplayComponentConfig?.onChange) {
                let onChangeHandler = field.flowCustomDisplayComponentConfig.onChange;
                console.log('Field has custom onchange handler', onChangeHandler);
                if (typeof onChangeHandler === 'string' && onChangeHandler.match(/function\(/)) {
                    try {
                        // tslint:disable-next-line:no-eval
                        let onChangeHandlerFunction = null;
                        // tslint:disable-next-line:no-eval
                        eval('onChangeHandlerFunction=' + onChangeHandler);
                        console.log('custom onchange handler - postEval');

                        // @ts-ignore
                        customOnChangeState = onChangeHandlerFunction ? onChangeHandlerFunction(value, updatedState, form) : {};
                        console.log('custom onchange handler - state', customOnChangeState);
                    } catch (e) {
                        console.log(e);
                        customOnChangeState = {};
                    }
                }
            }

            return {
                ...updatedState,
                fieldValues: {
                    ...updatedState.fieldValues,
                    ...calculatedFields,
                    ...customOnChangeState
                },
            };
        });
    };
};

// reset the fields state
/**
 * Handles field onBlur event inside a Form component
 *
 * @return function with the following signature:
 * (field: Field): () => void
 */
export const formComponentHandleBlur = (form: any, formState: any, setFormState: any, step: any, showAdministrativeFields: boolean, showPrivate: boolean) => (field: Field): () => void => {
    return (stateUpdates: object = {}) => {
        // JDM - merge stateUpdates with formstate to get the current value of the field
        let value = fieldValue({...formState, fieldValues: {...formState.fieldValues, ...stateUpdates}}, field);

        // format number
        if (field.type === 'number') {
            value = formatNumberField(field, numberFromText(value));
        }

        const visibilityState = applyVisibilityRules(form, step, formState, showAdministrativeFields, showPrivate);
        let newState = {
            ...formState,
            fieldValues: {
                ...formState.fieldValues,
                ...stateUpdates,
                [field.name]: value
            },
            fieldState: {
                ...formState.fieldState,
                [field.name]: {...initialFieldState}
            },
            visibility: visibilityState
        };

        // some field calculations depend on visibility state which may not be accurate during onChange
        console.log('form onblur calculationFieldValues');
        let calculatedFields = calculationFieldValues(form, newState, form.calculationFields);

        setFormState((state: any) => ({
            ...state,
            fieldValues: {
                ...state.fieldValues,
                ...stateUpdates,
                ...calculatedFields,
                [field.name]: value
            },
            fieldState: {
                ...state.fieldState,
                [field.name]: {...initialFieldState}
            },
            visibility: visibilityState
        }));

        // loop through all fields dependant on the blurred field and process
        /*
        form.fields
            // tslint:disable-next-line:triple-equals
            .filter((item: Field) => item.flowValueDependencyParentFieldId == field.id)
            .forEach((dependentField: Field) => {
                const value = fieldValue(formState, field);
                const resultProperty = dependentField.flowValueDependencyEtlConfig.resultProperty || 'result';
                const resultValueProperty = dependentField.flowValueDependencyEtlConfig.resultValueProperty || 'name';
                const resultTextProperty = dependentField.flowValueDependencyEtlConfig.resultTextProperty || 'name';
                Etl.getInstance().process(dependentField.flowValueDependencyEtlConfig.pipeline, value)
                    .then((response: EtlResponse) => {

                        const selectedOption = (response.json.result?.length > 0) ? {
                                value: response.json.result[0].value_1,
                                text: response.json.result[0].value_1,
                                label: response.json.result[0].value_1
                            } : null;

                            setFormState((state: FormState): FormState => {
                                console.log('setting ' + dependentField.name, selectedOption, ' to ', state);
                                return {
                                ...state,
                                fieldValues: {
                                    ...state.fieldValues,
                                    [dependentField.name]: selectedOption
                                }
                            }
                            });
                    })
                    .catch((error: any) => {
                        console.log(error);
                    });
            });
                 */
    };
};